import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import * as XLSX from "xlsx";
import {
  FileSelectEvent,
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from "primeng/fileupload";
import { SalesOrderExcelTrackingDto } from "../../../../core/models/orders/salesOrderExcelTrackingDto";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";

@Component({
  selector: "jni-order-tracking-excel",
  templateUrl: "./order-tracking-excel.component.html",
  styleUrls: ["./order-tracking-excel.component.scss"],
  imports: [FileUploadModule, CommonModule, FormsModule, ButtonModule],
})
export class OrderTrackingExcelComponent {
  @ViewChild("workflowFileUpload") fileUpload!: FileUpload;
  uploadedFiles: File[] = [];

  @Input() isProofUpload = false;
  proofComment = "";

  @Output() closeEvent = new EventEmitter();
  @Output() excelData = new EventEmitter<SalesOrderExcelTrackingDto[]>();
  @Output() downloadExcelTemplateEvent = new EventEmitter();
  @Output() proofFileSelected = new EventEmitter<File>();

  onClose() {
    this.closeEvent.emit();
  }

  /**
   * Retains a selected proof attachment (PDF/PNG/JPG) without spreadsheet parsing
   * and exposes it to the parent for submission.
   * @param event The PrimeNG file-select event.
   */
  onProofSelect(event: FileSelectEvent) {
    const file = event.currentFiles?.[0];
    if (!file) {
      return;
    }
    this.uploadedFiles = [file];
    this.proofFileSelected.emit(file);
  }

  onUpload(event: FileUploadHandlerEvent) {
    const file = event.files[0];
    this.uploadedFiles = [file]; // Add file to uploadedFiles array for display
    if (this.isProofUpload) {
      // Proof attachments must not be parsed as spreadsheets.
      this.proofFileSelected.emit(file);
      return;
    }
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e: ProgressEvent<FileReader>) => {
      /* create workbook */
      const binarystr: string | ArrayBuffer | null | undefined =
        e.target?.result;
      const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary" });

      /* selected the first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      /* save data */
      const data = XLSX.utils.sheet_to_json(ws) as SalesOrderExcelTrackingDto[]; // to get 2d array pass 2nd parameter as object {header: 1}

      this.excelData.emit(data);
    };
    this.clearUploadFile();
  }

  downloadTemplate() {
    this.downloadExcelTemplateEvent.emit();
  }

  isDragOver = false;

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Ignore leave events fired when moving over child elements.
    const container = event.currentTarget as HTMLElement | null;
    const next = event.relatedTarget as Node | null;
    if (container && next && container.contains(next)) {
      return;
    }
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.isProofUpload) {
      // Reuse PrimeNG's select flow so accept-type validation and onSelect match click-to-choose.
      if (event.dataTransfer?.files?.length === 1) {
        this.fileUpload?.onFileSelect(event);
      }
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (
        !file.name.toLowerCase().endsWith(".xlsx") &&
        !file.name.toLowerCase().endsWith(".xls")
      ) {
        // You might want to add a toastr service here for error messages
        console.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        console.error("File size must be less than 10MB");
        return;
      }

      if (this.fileUpload) {
        this.fileUpload.files = [file];
        // Trigger the upload
      }
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  removeFile(): void {
    this.uploadedFiles = [];
    // Clear PrimeNG's internal file list
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  /**
   * Clears the selected proof attachment and comment so the upload dialog opens fresh for each
   * line item. Called by the parent when a new proof upload dialog is shown.
   */
  resetProofUpload(): void {
    this.uploadedFiles = [];
    this.proofComment = "";
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  clearUploadFile() {
    this.uploadedFiles = [];
    // Clear PrimeNG's internal file list
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }
}
